const TOKEN_KEY = 'github_trends_token'
const VISITOR_KEY = 'github_trends_visitor_id'
export const FORGOT_PASSWORD_TIMEOUT_MS = 15_000
const importMetaEnv = import.meta.env || {}
const DEFAULT_API_BASE_URL = '/api'

function normalizeApiBaseUrl(value) {
  const normalized = String(value || '').trim()
  if (!normalized || normalized === '/' || normalized === '.') return DEFAULT_API_BASE_URL
  return normalized.replace(/\/+$/, '') || DEFAULT_API_BASE_URL
}

export const API_BASE_URL = normalizeApiBaseUrl(importMetaEnv.VITE_API_BASE_URL)

function apiUrl(path) {
  if (/^https?:\/\//i.test(path)) return path
  const normalizedPath = String(path).replace(/^\/+/, '').replace(/^api\/+/, '')
  return `${API_BASE_URL}/${normalizedPath}`
}

export class ApiError extends Error {
  constructor(message, status, code, details) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

function readStorage(storage, key) {
  try {
    return storage?.getItem(key) || ''
  } catch {
    return ''
  }
}

function writeStorage(storage, key, value) {
  try {
    if (value) storage?.setItem(key, value)
    else storage?.removeItem(key)
  } catch {
    // Storage can be blocked in private or hardened browser contexts.
  }
}

export function getToken() {
  const sessionToken = readStorage(globalThis.sessionStorage, TOKEN_KEY)
  if (sessionToken) return sessionToken

  const legacyToken = readStorage(globalThis.localStorage, TOKEN_KEY)
  if (legacyToken) {
    writeStorage(globalThis.sessionStorage, TOKEN_KEY, legacyToken)
    writeStorage(globalThis.localStorage, TOKEN_KEY, '')
    return legacyToken
  }

  return ''
}

export function setToken(token) {
  writeStorage(globalThis.sessionStorage, TOKEN_KEY, token)
  writeStorage(globalThis.localStorage, TOKEN_KEY, '')
}

export function getVisitorId() {
  try {
    const stored = localStorage.getItem(VISITOR_KEY)
    if (stored) return stored
    const generated = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `visitor_${Date.now()}_${Math.random().toString(36).slice(2)}`
    localStorage.setItem(VISITOR_KEY, generated)
    return generated
  } catch {
    return ''
  }
}

function getCurrentPagePath() {
  if (typeof window === 'undefined') return ''
  const hashPath = window.location.hash
    ? window.location.hash.replace(/^#/, '').split('?')[0]
    : ''
  return hashPath || window.location.pathname || ''
}

async function request(path, options = {}, responseMode = 'standard') {
  const token = getToken()
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
  let response
  try {
    response = await fetch(apiUrl(path), {
      ...options,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    })
  } catch (error) {
    if (error.name === 'TimeoutError') {
      throw new ApiError('璇锋眰瓒呮椂锛岃绋嶅悗閲嶈瘯', 0, 'REQUEST_TIMEOUT')
    }
    if (error.name === 'AbortError') throw error
    throw new ApiError('鎺ュ彛鍔犺浇澶辫触锛岃閲嶈瘯', 0, 'NETWORK_ERROR')
  }

  const responseText = await response.text()
  let payload = null
  try {
    payload = responseText ? JSON.parse(responseText) : null
  } catch {
    payload = null
  }
  if (!payload) {
    const contentType = response.headers.get('content-type') || 'unknown'
    throw new ApiError(
      `后端返回了非 JSON 响应（HTTP ${response.status}，${contentType}）`,
      response.status,
      'INVALID_RESPONSE',
    )
  }
  if (!response.ok || (responseMode === 'standard' && !payload.ok)) {
    const apiError = payload.error || {}
    if (response.status === 401) {
      setToken('')
      window.dispatchEvent(new Event('auth:expired'))
    }
    throw new ApiError(
      apiError.message || '璇锋眰澶辫触锛岃绋嶅悗閲嶈瘯',
      response.status,
      apiError.code || 'REQUEST_FAILED',
      apiError.details,
    )
  }
  return responseMode === 'raw' ? payload : payload.data
}

export function register(credentials, signal) {
  return request('/auth/register', {
    method: 'POST',
    body: JSON.stringify(credentials),
    signal,
  })
}

export function login(credentials, signal) {
  return request('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
    signal,
  })
}

export function sendVerificationEmail(email, signal) {
  return request('/auth/send-verification-email', {
    method: 'POST',
    body: JSON.stringify({ email }),
    signal,
  })
}

export function verifyEmail(token, signal) {
  return request('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
    signal,
  })
}

export function forgotPassword(email, signal) {
  const timeoutSignal = AbortSignal.timeout(FORGOT_PASSWORD_TIMEOUT_MS)
  return request('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
    signal: signal
      ? AbortSignal.any([signal, timeoutSignal])
      : timeoutSignal,
  })
}

export function resetPassword(token, password, signal) {
  return request('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
    signal,
  })
}

export function startGithubOAuth(signal) {
  return request('/auth/github', { signal })
}

export function normalizeGithubOAuthStatus(payload) {
  const status = payload?.data && typeof payload.data === 'object'
    ? payload.data
    : payload
  return { enabled: status?.enabled === true }
}

export async function getGithubOAuthStatus(signal) {
  const payload = await request('/auth/github/status', { signal }, 'raw')
  return normalizeGithubOAuthStatus(payload)
}

export function exchangeGithubOAuth(code, state, signal) {
  return request('/auth/github/exchange', {
    method: 'POST',
    body: JSON.stringify({ code, state }),
    signal,
  })
}

export function getMe(signal) {
  return request('/auth/me', { signal })
}

export function getMyGithubRepos(signal) {
  return request('/me/github/repos', { signal })
}

export function analyzeMyGithubRepo(fullName, force = false, signal) {
  return request('/me/github/repos/analyze', {
    method: 'POST',
    body: JSON.stringify({ fullName, force }),
    signal,
  })
}

export function getGithubAnalysis(signal) {
  return request('/github-analysis', { signal })
}

export function refreshGithubAnalysis(githubUsername, signal) {
  return request('/github-analysis/refresh', {
    method: 'POST',
    body: JSON.stringify({ githubUsername }),
    signal,
  })
}

export const DEFAULT_ANALYSIS_USAGE = {
  used: 0,
  remaining: 10,
  limit: 10,
  canAnalyze: true,
}

export function getArrayItems(value) {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.items)) return value.items
  if (Array.isArray(value?.repositories)) return value.repositories
  if (Array.isArray(value?.reports)) return value.reports
  if (Array.isArray(value?.data?.items)) return value.data.items
  if (Array.isArray(value?.data?.repositories)) return value.data.repositories
  if (Array.isArray(value?.data?.reports)) return value.data.reports
  return []
}

function normalizeAnalysisUsage(value) {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {}
  return {
    used: Number.isFinite(Number(source.used)) ? Number(source.used) : DEFAULT_ANALYSIS_USAGE.used,
    remaining: source.remaining === undefined
      ? DEFAULT_ANALYSIS_USAGE.remaining
      : source.remaining === null ? null : Number(source.remaining),
    limit: source.limit === undefined
      ? DEFAULT_ANALYSIS_USAGE.limit
      : source.limit === null ? null : Number(source.limit),
    canAnalyze: source.canAnalyze === undefined ? DEFAULT_ANALYSIS_USAGE.canAnalyze : Boolean(source.canAnalyze),
  }
}

export function isFeatureLimitExceeded(error) {
  return error?.code === 'FEATURE_LIMIT_EXCEEDED'
}

export async function getProjectHealthReports(signal) {
  try {
    const response = await request('/project-health/reports', { signal }, 'raw')
    const payload = response?.data ?? response ?? {}
    const items = getArrayItems(payload)
    return {
      ...payload,
      items,
      usage: normalizeAnalysisUsage(payload?.usage),
    }
  } catch (error) {
    if (error.name === 'AbortError') throw error
    console.warn('Failed to load project health reports', error)
    return {
      items: [],
      usage: DEFAULT_ANALYSIS_USAGE,
      error: error?.message || 'Load failed',
    }
  }
}

export async function getMyGithubRepositories(signal) {
  try {
    const res = await request('/me/github-repositories', { signal }, 'raw')
    const body = res?.data && typeof res.data === 'object' ? res.data : res
    const directItems = getArrayItems(body)
    const items = directItems.length > 0
      ? directItems
      : Array.isArray(body?.repositories)
        ? body.repositories
        : []
    const repositories = Array.isArray(body?.repositories)
      ? body.repositories
      : items
    return {
      ok: res?.ok ?? true,
      items,
      repositories,
      total: body?.total ?? res?.total ?? items.length,
      message: body?.message || res?.message || '',
    }
  } catch (error) {
    console.warn('Failed to load my GitHub repositories', error)
    return {
      ok: false,
      items: [],
      repositories: [],
      total: 0,
      error: error?.message || 'Load failed',
    }
  }
}

export function syncMyGithubRepositories(username = '', signal) {
  return request('/me/github-repositories/sync', {
    method: 'POST',
    body: JSON.stringify({ username }),
    signal,
  })
}

function resolveOptionsAndSignal(optionsOrSignal, fallbackSignal) {
  if (
    typeof AbortSignal !== 'undefined'
    && optionsOrSignal instanceof AbortSignal
  ) {
    return [{}, optionsOrSignal]
  }
  return [optionsOrSignal || {}, fallbackSignal]
}

function normalizeRequestLanguage(language) {
  return language === 'en' ? 'en' : 'zh'
}

export function createProjectHealthReport(repoUrl, optionsOrSignal, signal) {
  const [options, requestSignal] = resolveOptionsAndSignal(optionsOrSignal, signal)
  return request('/project-health/reports', {
    method: 'POST',
    body: JSON.stringify({
      repoUrl,
      language: normalizeRequestLanguage(options.language),
    }),
    signal: requestSignal,
  })
}

export function getProjectHealthReport(reportId, signal) {
  return request(`/project-health/reports/${encodeURIComponent(reportId)}`, { signal })
}

export function getPublicProjectHealthReport(reportId, signal) {
  return request(`/project-health/reports/public/${encodeURIComponent(reportId)}`, { signal })
}

export function refreshProjectHealthReport(reportId, optionsOrSignal, signal) {
  const [options, requestSignal] = resolveOptionsAndSignal(optionsOrSignal, signal)
  return request(`/project-health/reports/${encodeURIComponent(reportId)}/refresh`, {
    method: 'POST',
    body: JSON.stringify({ language: normalizeRequestLanguage(options.language) }),
    signal: requestSignal,
  })
}

export function deleteProjectHealthReport(reportId, signal) {
  return request(`/project-health/reports/${encodeURIComponent(reportId)}`, {
    method: 'DELETE',
    signal,
  })
}

export function analyzeResume(file, options = {}, signal) {
  const formData = new FormData()
  formData.append('resume', file)
  formData.append('targetRole', options.targetRole || '')
  formData.append('targetGoal', options.targetGoal || '')
  formData.append('growthTarget', options.growthTarget || '')
  formData.append('githubUrl', options.githubUrl || '')
  formData.append('locale', options.locale || 'zh')
  return request('/resume/analyze', {
    method: 'POST',
    body: formData,
    signal,
  })
}

export function generateResumeProjectSection(payload = {}, signal) {
  return request('/resume/generate', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function optimizeResumeSection(payload = {}, signal) {
  return request('/resume/optimize-section', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function recordResumeDraftUsage(signal) {
  return request('/resume/draft-usage', {
    method: 'POST',
    body: JSON.stringify({}),
    signal,
  })
}

export function recordResumeExportUsage(signal) {
  return request('/resume/export-usage', {
    method: 'POST',
    body: JSON.stringify({}),
    signal,
  })
}

export function getResumeReports(signal) {
  return request('/resume/reports', { signal })
}

export function getResumeReport(reportId, signal) {
  return request(`/resume/reports/${encodeURIComponent(reportId)}`, { signal })
}

export function deleteResumeReport(reportId, signal) {
  return request(`/resume/reports/${encodeURIComponent(reportId)}`, {
    method: 'DELETE',
    signal,
  })
}

export function compareLatestResumeReports(signal) {
  return request('/resume/reports/compare/latest', { signal })
}

function paginationQuery(page = 1, pageSize = 10) {
  return `page=${encodeURIComponent(page)}&pageSize=${encodeURIComponent(pageSize)}`
}

export function getDaily(date, page = 1, pageSize = 10, sort = 'heat', signal) {
  return request(
    `/repos/daily?date=${encodeURIComponent(date)}&sort=${encodeURIComponent(sort)}&${paginationQuery(page, pageSize)}`,
    { signal },
  )
}

export function getWeekly(page = 1, pageSize = 10, signal) {
  return request(`/repos/weekly?${paginationQuery(page, pageSize)}`, { signal })
}

export function getAllTime(page = 1, pageSize = 10, signal) {
  return request(`/repos/all-time?${paginationQuery(page, pageSize)}`, { signal })
}

export function getRepo(id, signal) {
  return request(`/repos/${encodeURIComponent(id)}`, { signal })
}

export function getTools(options = {}, signal) {
  const params = new URLSearchParams({
    page: String(options.page || 1),
    pageSize: String(options.pageSize || 12),
  })
  if (options.category) params.set('category', options.category)
  if (options.q) params.set('q', options.q)
  return request(`/tools?${params.toString()}`, { signal })
}

export function getTool(id, signal) {
  return request(`/tools/${encodeURIComponent(id)}`, { signal })
}

export function getToolCategories(signal) {
  return request('/tools/categories', { signal })
}

export function getToolsByCategory(category, options = {}, signal) {
  const params = new URLSearchParams({
    page: String(options.page || 1),
    pageSize: String(options.pageSize || 12),
  })
  if (options.q) params.set('q', options.q)
  return request(
    `/tools/category/${encodeURIComponent(category)}?${params.toString()}`,
    { signal },
  )
}

export function getToolAlternatives(options = {}, signal) {
  const params = new URLSearchParams({
    page: String(options.page || 1),
    pageSize: String(options.pageSize || 12),
  })
  if (options.q) params.set('q', options.q)
  return request(`/tools/alternatives?${params.toString()}`, { signal })
}

export function getRecommendedTools(options = {}, signal) {
  const params = new URLSearchParams({
    page: String(options.page || 1),
    pageSize: String(options.pageSize || 12),
  })
  if (options.category) params.set('category', options.category)
  if (options.q) params.set('q', options.q)
  return request(`/tools/recommended?${params.toString()}`, { signal })
}

export function getDailyToolDigest(signal) {
  return request('/tools/daily', { signal })
}

export function getUserPreferences(signal) {
  return request('/user/preferences', { signal })
}

export function saveUserPreferences(preferences, signal) {
  return request('/user/preferences', {
    method: 'POST',
    body: JSON.stringify(preferences),
    signal,
  })
}

export function toolDownloadUrl(id, type = 'latest') {
  return apiUrl(`/tools/${encodeURIComponent(id)}/download/${encodeURIComponent(type)}`)
}

export function recordToolInteraction(id, type, targetUrl = '', signal) {
  return request(`/tools/${encodeURIComponent(id)}/click`, {
    method: 'POST',
    body: JSON.stringify({ type, targetUrl }),
    signal,
  })
}

export function recordAnalyticsEvent(eventName, metadata = {}, optionsOrSignal) {
  const signal = typeof AbortSignal !== 'undefined' && optionsOrSignal instanceof AbortSignal
    ? optionsOrSignal
    : optionsOrSignal?.signal
  const options = signal === optionsOrSignal ? {} : (optionsOrSignal || {})
  const visitorId = options.visitorId || getVisitorId()
  const path = options.path
    || metadata.page_path
    || metadata.page
    || getCurrentPagePath()
  const targetType = options.targetType || metadata.targetType || ''
  const targetId = options.targetId ?? metadata.targetId ?? null
  const enrichedMetadata = {
    ...metadata,
    ...(path ? { page_path: path } : {}),
    ...(targetType ? { targetType } : {}),
    ...(targetId !== null && targetId !== undefined && targetId !== '' ? { targetId } : {}),
    ...(visitorId ? { visitorId, visitor_id: visitorId } : {}),
  }

  return request('/analytics/events', {
    method: 'POST',
    body: JSON.stringify({
      eventName,
      eventType: options.eventType || eventName,
      metadata: enrichedMetadata,
      path,
      targetId,
      target_id: targetId,
      targetType,
      target_type: targetType,
      visitorId,
      visitor_id: visitorId,
    }),
    signal,
  })
}

export function submitUserFeedback(payload = {}, signal) {
  return request('/feedback', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getAdminFeedback(options = {}, signal) {
  const params = new URLSearchParams({
    page: String(options.page || 1),
    pageSize: String(options.pageSize || 50),
  })
  if (options.status) params.set('status', options.status)
  if (options.type) params.set('type', options.type)
  return request(`/admin/feedback?${params.toString()}`, { signal })
}

export function submitGithubAnalysisFeedback({
  analysisId = null,
  feedback = '',
  path = '',
  repoUrl = '',
  visitorId = '',
} = {}, signal) {
  const normalizedAnalysisId = analysisId ? String(analysisId) : null
  const pagePath = path || getCurrentPagePath()
  const resolvedVisitorId = visitorId || getVisitorId()

  return request('/analytics/events', {
    method: 'POST',
    body: JSON.stringify({
      eventType: 'github_analysis_feedback',
      targetType: 'github_analysis',
      targetId: normalizedAnalysisId,
      path: pagePath,
      visitorId: resolvedVisitorId,
      metadata: {
        feedback,
        repoUrl: repoUrl || '',
        analysisId: normalizedAnalysisId,
      },
    }),
    signal,
  })
}

export function getMyPet(signal) {
  return request('/pet/me', { signal })
}

export function checkinPet(signal) {
  return request('/pet/checkin', {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function feedPet(signal) {
  return request('/pet/feed', {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function updatePet(payload, signal) {
  return request('/pet/update', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
    signal,
  })
}

export function rewardPetTask(payload, signal) {
  return request('/pet/task-reward', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
    signal,
  })
}

export function getUserToolbox(signal) {
  return request('/user/toolbox', { signal })
}

export function setToolboxItem(repoId, status = 'favorite', signal) {
  return request(`/user/toolbox/${encodeURIComponent(repoId)}`, {
    method: 'POST',
    body: JSON.stringify({ status }),
    signal,
  })
}

export function removeToolboxItem(repoId, signal) {
  return request(`/user/toolbox/${encodeURIComponent(repoId)}`, {
    method: 'DELETE',
    signal,
  })
}

export function followTool(repoId, signal) {
  return request(`/tools/${encodeURIComponent(repoId)}/follow`, {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function unfollowTool(repoId, signal) {
  return request(`/tools/${encodeURIComponent(repoId)}/follow`, {
    method: 'DELETE',
    signal,
  })
}

export function getFollowedToolUpdates(signal) {
  return request('/user/tool-updates', { signal })
}

export function getUserExploration(signal) {
  return request('/user/exploration', { signal })
}

export function getRepoViews(id, signal) {
  return request(`/repos/${encodeURIComponent(id)}/views`, { signal })
}

export function createSubscription(email, frequency = 'weekly', signal) {
  return request('/subscriptions', {
    method: 'POST',
    body: JSON.stringify({ email, frequency }),
    signal,
  }, 'raw')
}

export function getRecentRepoViews(signal) {
  return request('/user/recent-views', { signal })
}

export function refreshDaily(payload, signal) {
  return request('/repos/refresh', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function publishDaily(date, signal) {
  return request('/repos/publish', {
    method: 'POST',
    body: JSON.stringify({ date }),
    signal,
  })
}

export function getDailyStatus(date, signal) {
  return request(`/admin/daily-status?date=${encodeURIComponent(date)}`, { signal })
}

export function getRankingStatus(date, signal) {
  return request(`/admin/rankings/status?date=${encodeURIComponent(date)}`, { signal })
}

export function getRankingMetrics(date, signal) {
  return request(`/admin/rankings/metrics?date=${encodeURIComponent(date)}`, { signal })
}

export function getRankingJobs(page = 1, pageSize = 10, signal) {
  return request(`/admin/rankings/jobs?${paginationQuery(page, pageSize)}`, { signal })
}

export function getAdminDailyRanking(date, page = 1, pageSize = 10, signal) {
  return request(
    `/admin/rankings/daily?date=${encodeURIComponent(date)}&${paginationQuery(page, pageSize)}`,
    { signal },
  )
}

export function getAdminWeeklyRanking(date, page = 1, pageSize = 10, signal) {
  return request(
    `/admin/rankings/weekly?date=${encodeURIComponent(date)}&${paginationQuery(page, pageSize)}`,
    { signal },
  )
}

export function getAdminAllTimeRanking(page = 1, pageSize = 10, signal) {
  return request(
    `/admin/rankings/all-time?${paginationQuery(page, pageSize)}`,
    { signal },
  )
}

export function getRankingHealth(signal) {
  return request('/admin/rankings/health', { signal })
}

export function getAdminAnalyticsOverview(signal) {
  return request('/admin/analytics/overview', { signal })
}

export function getAdminAnalyticsTopRepos(signal) {
  return request('/admin/analytics/top-repos', { signal })
}

export function getAdminAnalyticsSubscriptions(signal) {
  return request('/admin/analytics/subscriptions', { signal })
}

export function getAdminV2Analytics(signal) {
  return request('/admin/analytics/v2', { signal })
}

export function getAdminUsers(options = {}, signal) {
  const params = new URLSearchParams({
    page: String(options.page || 1),
    pageSize: String(options.pageSize || 10),
    sort: options.sort || 'created_at',
  })
  if (options.keyword) params.set('keyword', options.keyword)
  if (options.provider) params.set('provider', options.provider)
  return request(`/admin/users?${params.toString()}`, { signal })
}

export function getAdminUserMetrics(signal) {
  return request('/admin/users/metrics', { signal })
}

export function updateAdminUserMembership(userId, payload = {}, signal) {
  return request(`/admin/users/${encodeURIComponent(userId)}/membership`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getVipPlans(signal) {
  return request('/vip/plans', { signal })
}

export function getVipMe(signal) {
  return request('/vip/me', { signal })
}

export function createVipOrder(planId, signal) {
  return request('/vip/orders', {
    method: 'POST',
    body: JSON.stringify({ planId }),
    signal,
  })
}

export function getVipOrders(signal) {
  return request('/vip/orders', { signal })
}

export function getVipOrder(orderNo, signal) {
  return request(`/vip/orders/${encodeURIComponent(orderNo)}`, { signal })
}

export function submitVipOrderProof(orderNo, payload = {}, signal) {
  return request(`/vip/orders/${encodeURIComponent(orderNo)}/proof`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function requestVipOrderRefund(orderNo, payload = {}, signal) {
  return request(`/vip/orders/${encodeURIComponent(orderNo)}/refund-request`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export async function fetchVipPaymentQr(endpoint, signal) {
  const token = getToken()
  const response = await fetch(apiUrl(endpoint), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  })
  if (!response.ok) {
    let payload = null
    try {
      payload = await response.json()
    } catch {
      payload = null
    }
    const apiError = payload?.error || {}
    if (response.status === 401) {
      setToken('')
      window.dispatchEvent(new Event('auth:expired'))
    }
    throw new ApiError(
      apiError.message || '收款码暂不可用，请联系客服。',
      response.status,
      apiError.code || 'VIP_PAYMENT_QR_FAILED',
      apiError.details,
    )
  }
  return response.blob()
}

export function getAdminVipOrders(options = {}, signal) {
  const params = new URLSearchParams({
    page: String(options.page || 1),
    pageSize: String(options.pageSize || 50),
  })
  if (options.status) params.set('status', options.status)
  return request(`/admin/vip/orders?${params.toString()}`, { signal })
}

export function confirmAdminVipOrder(orderNo, payload = {}, signal) {
  return request(`/admin/vip/orders/${encodeURIComponent(orderNo)}/confirm`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function rejectAdminVipOrder(orderNo, payload = {}, signal) {
  return request(`/admin/vip/orders/${encodeURIComponent(orderNo)}/reject`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function markRefundedAdminVipOrder(orderNo, payload = {}, signal) {
  return request(`/admin/vip/orders/${encodeURIComponent(orderNo)}/mark-refunded`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getAdminVipMemberships(options = {}, signal) {
  const params = new URLSearchParams({
    page: String(options.page || 1),
    pageSize: String(options.pageSize || 50),
  })
  return request(`/admin/vip/memberships?${params.toString()}`, { signal })
}

export function grantAdminVipUser(userId, payload = {}, signal) {
  return request(`/admin/vip/users/${encodeURIComponent(userId)}/grant`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function revokeAdminVipUser(userId, payload = {}, signal) {
  return request(`/admin/vip/users/${encodeURIComponent(userId)}/revoke`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getAdminVipLogs(options = {}, signal) {
  const params = new URLSearchParams({
    page: String(options.page || 1),
    pageSize: String(options.pageSize || 50),
  })
  return request(`/admin/vip/logs?${params.toString()}`, { signal })
}

export function createSupportTicket(payload = {}, signal) {
  return request('/support/tickets', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getSupportTickets(signal) {
  return request('/support/tickets', { signal })
}

export function getSupportTicket(ticketId, signal) {
  return request(`/support/tickets/${encodeURIComponent(ticketId)}`, { signal })
}

export function getAdminSupportTickets(options = {}, signal) {
  const params = new URLSearchParams({
    page: String(options.page || 1),
    pageSize: String(options.pageSize || 50),
  })
  if (options.status) params.set('status', options.status)
  if (options.type) params.set('type', options.type)
  return request(`/admin/support/tickets?${params.toString()}`, { signal })
}

export function updateAdminSupportTicket(ticketId, payload = {}, signal) {
  return request(`/admin/support/tickets/${encodeURIComponent(ticketId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    signal,
  })
}

export function resolveAdminSupportTicket(ticketId, payload = {}, signal) {
  return request(`/admin/support/tickets/${encodeURIComponent(ticketId)}/resolve`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function rejectAdminSupportTicket(ticketId, payload = {}, signal) {
  return request(`/admin/support/tickets/${encodeURIComponent(ticketId)}/reject`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function markRefundedAdminSupportTicket(ticketId, payload = {}, signal) {
  return request(`/admin/support/tickets/${encodeURIComponent(ticketId)}/mark-refunded`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function generateDailyRanking(payload, signal) {
  return request('/admin/rankings/daily/generate', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function publishDailyRanking(payload, signal) {
  return request('/admin/rankings/daily/publish', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function publishWeeklyRanking(payload, signal) {
  return request('/admin/rankings/weekly/publish', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function refreshWeeklyRanking(payload, signal) {
  return request('/admin/rankings/weekly/refresh', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function refreshAllTimeRanking(payload = {}, signal) {
  return request('/admin/rankings/all-time/refresh', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function importAllTimeProjects(payload = {}, signal) {
  return request('/admin/rankings/all-time/import', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function generateAllTimeInsights(payload, signal) {
  return request('/admin/rankings/all-time/insights', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function regenerateExplanation(repoId, date, signal) {
  return request(`/repos/${encodeURIComponent(repoId)}/explain`, {
    method: 'POST',
    body: JSON.stringify({ date }),
    signal,
  })
}

export function addFavorite(repoId) {
  return request(`/favorites/${encodeURIComponent(repoId)}`, {
    method: 'POST',
    body: '{}',
  })
}

export function removeFavorite(repoId) {
  return request(`/favorites/${encodeURIComponent(repoId)}`, {
    method: 'DELETE',
  })
}

export function getFavorites(signal) {
  return request('/favorites', { signal })
}

export function searchRepos(query, signal) {
  return request(`/search?q=${encodeURIComponent(query)}`, { signal })
}

export function chatWithAssistant(payload, signal) {
  return request('/ai/assistant/chat', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getAiConversations(signal) {
  return request('/ai/conversations', { signal })
}

export function createAiConversation(signal) {
  return request('/ai/conversations', {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function getAiConversationMessages(conversationId, signal) {
  return request(
    `/ai/conversations/${encodeURIComponent(conversationId)}/messages`,
    { signal },
  )
}

export function sendAiConversationMessage(conversationId, payload, signal) {
  return request(
    `/ai/conversations/${encodeURIComponent(conversationId)}/messages`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      signal,
    },
  )
}

export function deleteAiConversation(conversationId, signal) {
  return request(
    `/ai/conversations/${encodeURIComponent(conversationId)}`,
    {
      method: 'DELETE',
      signal,
    },
  )
}

export function parseCareerJob(payload, signal) {
  return request('/career/jobs/parse', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getCareerJobs(params = {}, signal) {
  const search = new URLSearchParams()
  if (params.page) search.set('page', params.page)
  if (params.pageSize) search.set('pageSize', params.pageSize)
  const suffix = search.toString() ? `?${search.toString()}` : ''
  return request(`/career/jobs${suffix}`, { signal })
}

export function getCareerJob(id, signal) {
  return request(`/career/jobs/${encodeURIComponent(id)}`, { signal })
}

export function createCareerResumeMatch(jobId, payload, signal) {
  return request(`/career/jobs/${encodeURIComponent(jobId)}/resume-match`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function createCareerInterviewPack(jobId, payload = {}, signal) {
  return request(`/career/jobs/${encodeURIComponent(jobId)}/interview-pack`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function createCareerInterview(payload, signal) {
  return request('/career/interviews', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getCareerInterview(id, signal) {
  return request(`/career/interviews/${encodeURIComponent(id)}`, { signal })
}

export function sendCareerInterviewMessage(id, payload, signal) {
  return request(`/career/interviews/${encodeURIComponent(id)}/messages`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function searchGithubTools(query, signal) {
  return request('/tools/github-search', {
    method: 'POST',
    body: JSON.stringify({ query }),
    signal,
  })
}

export function finishCareerInterview(id, signal) {
  return request(`/career/interviews/${encodeURIComponent(id)}/finish`, {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function generateCareerRoadmap(payload = {}, signal) {
  return request('/career/learning/generate', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function saveCareerRoadmap(payload = {}, signal) {
  return request('/career/learning/save', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export async function getCareerPlannerDirections(signal) {
  const payload = await request('/explore/directions', { signal }, 'raw')
  return Array.isArray(payload?.directions) ? payload.directions : []
}

export async function getCareerPlannerDirectionCatalog(signal) {
  const payload = await request('/explore/directions', { signal }, 'raw')
  return {
    categories: Array.isArray(payload?.categories) ? payload.categories : [],
    directions: Array.isArray(payload?.directions) ? payload.directions : [],
  }
}

export async function getCareerPlannerDirection(key, signal) {
  const payload = await request(`/explore/directions/${encodeURIComponent(key)}`, { signal }, 'raw')
  return payload?.direction || null
}

export async function createCareerPlannerPlan(direction, signal) {
  return request('/explore/plan', {
    method: 'POST',
    body: JSON.stringify({ direction }),
    signal,
  }, 'raw')
}

export function getStudentGrowthMe(signal) {
  return request('/student-growth/me', { signal })
}

function adaptStudentGrowthMeToSummary(payload = {}) {
  const source = payload && typeof payload === 'object' ? payload : {}
  const plan = source.activePlan || source.plan || null
  const profile = source.profile || {}
  const tasks = Array.isArray(source.todayTasks)
    ? source.todayTasks
    : Array.isArray(source.tasks)
      ? source.tasks
      : []
  const completedTasks = tasks.filter((task) => task?.status === 'completed').length
  const progress = Number(plan?.progress || source.stats?.progress || 0)

  return {
    profile,
    currentGoal: {
      title: plan?.targetDirection || profile?.careerGoal || profile?.goal || profile?.direction || '',
      source: plan ? 'student_growth_plans' : 'student_growth_profile',
    },
    growthStage: {
      stage: plan?.stage || profile?.level || '',
      progress,
      planId: plan?.id || null,
      planUpdatedAt: plan?.updatedAt || null,
      source: plan ? 'student_growth_plans' : 'student_growth_profile',
    },
    todayTasks: {
      summary: {
        total: tasks.length,
        completed: completedTasks,
        pending: tasks.length - completedTasks,
        estimatedMinutes: 0,
        xpReward: 0,
      },
      items: tasks.map((task) => ({
        id: String(task?.id || ''),
        title: task?.title || '',
        description: task?.description || '',
        taskType: task?.taskType || task?.type || '',
        status: task?.status || 'pending',
        estimatedMinutes: Number(task?.estimatedMinutes || 0),
        xpReward: Number(task?.xpReward || 0),
        completedAt: task?.completedAt || null,
      })),
    },
    nextStep: {
      type: tasks.some((task) => task?.status !== 'completed') ? 'today_task' : 'profile',
      title: tasks.find((task) => task?.status !== 'completed')?.title || plan?.targetDirection || profile?.goal || '',
      description: tasks.find((task) => task?.status !== 'completed')?.description || '',
      source: 'student_growth_me_fallback',
      targetPath: tasks.some((task) => task?.status !== 'completed') ? '/student-growth/today' : '/student-growth/profile',
    },
    learningProgress: {
      completedNodes: 0,
      totalNodes: 0,
      percent: progress,
      nextNode: null,
    },
    projectAssets: {
      total: 0,
      active: 0,
      completed: 0,
      averageProgress: 0,
      latest: [],
    },
    resumeAssets: {
      total: 0,
      latestScore: 0,
      latest: [],
    },
    dataSources: ['student_growth_me_fallback'],
  }
}

export async function getGrowthSummary(signal) {
  try {
    return await request('/growth/summary', { signal })
  } catch (error) {
    if (error?.status === 404) {
      const fallback = await getStudentGrowthMe(signal)
      return adaptStudentGrowthMeToSummary(fallback)
    }
    throw error
  }
}

export function getStudentGrowthProfile(signal) {
  return request('/student-growth/profile', { signal })
}

export function createStudentGrowthProfile(payload = {}, signal) {
  return request('/student-growth/profile', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function updateStudentGrowthProfile(payload = {}, signal) {
  return request('/student-growth/profile', {
    method: 'PUT',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getStudentCareerProfile(signal) {
  return request('/student-career-exploration/profile', { signal })
}

export function saveStudentCareerProfile(payload = {}, signal) {
  return request('/student-career-exploration/profile', {
    method: 'PUT',
    body: JSON.stringify(payload),
    signal,
  })
}

export function recommendStudentCareers(payload = {}, signal) {
  return request('/student-career-exploration/recommendations', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function recommendAiGrowthCareers(payload = {}, signal) {
  return request('/student-growth/careers/recommend', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function selectStudentCareerRecommendation(recommendationId, signal) {
  return request(`/student-career-exploration/recommendations/${encodeURIComponent(recommendationId)}/select`, {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function getKnowledgeMajors(signal) {
  return request('/knowledge/majors', { signal })
}

export function getKnowledgeCareerDetail(careerKey, signal) {
  return request(`/knowledge/careers/${encodeURIComponent(careerKey)}`, { signal })
}

export function getKnowledgeResources(params = {}, signal) {
  const query = new URLSearchParams(params).toString()
  return request(`/knowledge/resources${query ? `?${query}` : ''}`, { signal })
}

export function getKnowledgeCompetitions(params = {}, signal) {
  const query = new URLSearchParams(params).toString()
  return request(`/knowledge/competitions${query ? `?${query}` : ''}`, { signal })
}

export function getKnowledgeCertificates(params = {}, signal) {
  const query = new URLSearchParams(params).toString()
  return request(`/knowledge/certificates${query ? `?${query}` : ''}`, { signal })
}

export function getKnowledgeInterviewPack(params = {}, signal) {
  const query = new URLSearchParams(params).toString()
  return request(`/knowledge/interview-pack${query ? `?${query}` : ''}`, { signal })
}

export function analyzeResumeTarget(payload = {}, signal) {
  return request('/knowledge/resume-target-analysis', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getStudentRouteV2(signal) {
  return request('/student-growth/route-v2', { signal })
}

export function getStudentGrowthAssets(signal) {
  return request('/student-growth/assets', { signal })
}

export function getStudentGrowthWeeklyReport(signal) {
  return request('/student-growth/reports/weekly', { signal })
}

export function getStudentGrowthMentorAdvice(signal) {
  return request('/student-growth/mentor/advice', { signal })
}

export function generateStudentRouteV2(signal) {
  return request('/student-growth/route-v2/generate', {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function getStudentRouteV2Today(signal) {
  return request('/student-growth/route-v2/today', { signal })
}

export function completeStudentRouteV2Task(taskId, payload = {}, signal) {
  return request(`/student-growth/route-v2/tasks/${encodeURIComponent(taskId)}/complete`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getStudentLearningProfile(signal) {
  return getStudentGrowthProfile(signal)
}

export function saveStudentLearningProfile(payload = {}, signal) {
  return updateStudentGrowthProfile(payload, signal)
}

export function createGrowthAssessment(payload = {}, signal) {
  return request('/growth-assessments', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getGrowthAssessment(assessmentId, signal) {
  return request(`/growth-assessments/${encodeURIComponent(assessmentId)}`, { signal })
}

export function sendGrowthAssessmentMessage(assessmentId, payload = {}, signal) {
  return request(`/growth-assessments/${encodeURIComponent(assessmentId)}/messages`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function generateGrowthAssessmentReport(assessmentId, signal) {
  return request(`/growth-assessments/${encodeURIComponent(assessmentId)}/report`, {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function createGrowthCompanionSession(payload = {}, signal) {
  return request('/growth-companion/sessions', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getGrowthCompanionSession(sessionId, signal) {
  return request(`/growth-companion/sessions/${encodeURIComponent(sessionId)}`, { signal })
}

export function sendGrowthCompanionMessage(sessionId, payload = {}, signal) {
  return request(`/growth-companion/sessions/${encodeURIComponent(sessionId)}/messages`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function generateGrowthCompanionProfile(sessionId, signal) {
  return request(`/growth-companion/sessions/${encodeURIComponent(sessionId)}/profile`, {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function getGrowthCompanionMemories(signal) {
  return request('/growth-companion/memories', { signal })
}

export function getGrowthCompanionDailyAdvice(signal) {
  return request('/growth-companion/daily-advice', { signal })
}

export function applyGrowthPlanAdjustment(draftId, signal) {
  return request(`/growth-companion/plan-adjustments/${encodeURIComponent(draftId)}/apply`, {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function dismissGrowthPlanAdjustment(draftId, signal) {
  return request(`/growth-companion/plan-adjustments/${encodeURIComponent(draftId)}/dismiss`, {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function createStudentGrowthPlan(payload = {}, signal) {
  return request('/student-growth/plans', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function generateAiGrowthPlan(payload = {}, signal) {
  return request('/student-growth/plans/generate', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getLatestAiGrowthPlan(signal) {
  return request('/student-growth/plans/latest', { signal })
}

export function getLatestAiGrowthPlanGeneration(signal) {
  return request('/student-growth/plans/generations/latest', { signal })
}

export function getAiGrowthPlanVersions(signal) {
  return request('/student-growth/plans/versions', { signal })
}

export function restoreAiGrowthPlanVersion(versionId, expectedActivePlanId, signal) {
  return request(`/student-growth/plans/versions/${encodeURIComponent(versionId)}/restore`, {
    method: 'POST',
    body: JSON.stringify({ expectedActivePlanId }),
    signal,
  })
}

export function getAiGrowthTasks(signal) {
  return request('/student-growth/ai-tasks', { signal })
}

export function generateAiGrowthTasks(signal) {
  return request('/student-growth/ai-tasks/generate', {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function getAiGrowthTaskEvaluation(signal) {
  return request('/student-growth/ai-tasks/evaluation', { signal })
}

export function generateGrowthPlanAdjustment(payload = {}, signal) {
  return request('/growth-companion/plan-adjustments/generate', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function updateAiGrowthTask(taskId, payload = {}, signal) {
  return request(`/student-growth/ai-tasks/${encodeURIComponent(taskId)}/action`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function generateAiGrowthTaskQuestion(taskId, signal) {
  return request(`/student-growth/ai-tasks/${encodeURIComponent(taskId)}/question`, { method: 'POST', body: '{}', signal })
}

export function evaluateAiGrowthTaskAnswer(taskId, payload = {}, signal) {
  return request(`/student-growth/ai-tasks/${encodeURIComponent(taskId)}/answer`, { method: 'POST', body: JSON.stringify(payload), signal })
}

export function getLearningVideoRecommendations(query, signal) {
  return request(`/student-growth/video-recommendations?q=${encodeURIComponent(query || '')}`, { signal })
}

export function activateStudentGrowthPlan(planId, signal) {
  return request(`/student-growth/plans/${encodeURIComponent(planId)}/active`, {
    method: 'PATCH',
    body: '{}',
    signal,
  })
}

export function getTodayStudentGrowthTasks(signal) {
  return request('/student-growth/tasks/today', { signal })
}

export function getStudentGrowthToday(signal) {
  return request('/student-growth/today', { signal })
}

export function getStudentGrowthDashboard(signal) {
  return request('/student-growth/dashboard', { signal })
}

export function getGrowthEventsWeeklyReport(signal) {
  return request('/growth-events/weekly-report', { signal })
}

export function getGrowthEventsAbilityScore(signal) {
  return request('/growth-events/ability-score', { signal })
}

export function getGrowthEventsTodaySuggestion(signal) {
  return request('/growth-events/today-suggestion', { signal })
}

export function getGrowthMentorToday(signal) {
  return request('/growth-mentor/today', { signal })
}

export function completeStudentDailyTask(taskId, signal) {
  return request(`/student-growth/daily-tasks/${encodeURIComponent(taskId)}/complete`, {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function skipStudentDailyTask(taskId, signal) {
  return request(`/student-growth/daily-tasks/${encodeURIComponent(taskId)}/skip`, {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function completeStudentGrowthTask(taskId, signal) {
  return request(`/student-growth/tasks/${encodeURIComponent(taskId)}/complete`, {
    method: 'PATCH',
    body: '{}',
    signal,
  })
}

export function getStudentGrowthEvents(signal) {
  return request('/student-growth/events', { signal })
}

export function recordStudentGrowthEvent(payload = {}, signal) {
  return request('/student-growth/events', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function generateStudentChangePlan(payload = {}, signal) {
  return request('/student-growth/change-plan/generate', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function saveStudentChangePlan(payload = {}, signal) {
  return request('/student-growth/change-plan/save', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getLatestStudentChangePlan(signal) {
  return request('/student-growth/change-plan/latest', { signal })
}

export function getLearningPaths(signal) {
  return request('/learning-paths', { signal })
}

export function getLearningPath(pathId = 'java-backend', signal) {
  return request(`/learning-paths/${encodeURIComponent(pathId)}`, { signal })
}

export function getLearningNode(nodeId, signal) {
  return request(`/learning-paths/nodes/${encodeURIComponent(nodeId)}`, { signal })
}

export function getLearningNodePractices(nodeId, signal) {
  return request(`/learning/nodes/${encodeURIComponent(nodeId)}/practices`, { signal })
}

export function completeLearningNode(nodeId, signal) {
  return request(`/learning-paths/nodes/${encodeURIComponent(nodeId)}/complete`, {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function getLearningResource(resourceId, signal) {
  return request(`/learning-paths/resources/${encodeURIComponent(resourceId)}`, { signal })
}

export function completeLearningResource(resourceId, signal) {
  return request(`/learning-paths/resources/${encodeURIComponent(resourceId)}/complete`, {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function getLearningPractice(practiceId, signal) {
  return request(`/practice/tasks/${encodeURIComponent(practiceId)}`, { signal })
}

export function listLearningPractices(filters = {}, signal) {
  const search = new URLSearchParams()
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value) search.set(key, String(value))
  })
  const suffix = search.toString() ? `?${search.toString()}` : ''
  return request(`/practice/tasks${suffix}`, { signal })
}

export function submitLearningPractice(practiceId, payload = {}, signal) {
  return request(`/practice/tasks/${encodeURIComponent(practiceId)}/submit`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getPracticeCenterTasks(params = {}, signal) {
  const search = new URLSearchParams()
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value) search.set(key, String(value))
  })
  const suffix = search.toString() ? `?${search.toString()}` : ''
  return request(`/student-practice-center/tasks${suffix}`, { signal })
}

export function getPracticeCenterToday(signal) {
  return request('/student-practice-center/today', { signal })
}

export function getPracticeCenterTask(practiceId, signal) {
  return request(`/student-practice-center/tasks/${encodeURIComponent(practiceId)}`, { signal })
}

export function submitPracticeCenterAnswer(practiceId, payload = {}, signal) {
  return request(`/student-practice-center/tasks/${encodeURIComponent(practiceId)}/submit`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function runLearningPractice(practiceId, payload = {}, signal) {
  return request(`/practice/tasks/${encodeURIComponent(practiceId)}/run`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function runLearningPracticeTests(practiceId, payload = {}, signal) {
  return request(`/practice/tasks/${encodeURIComponent(practiceId)}/run-tests`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getStudentProjectTemplates(params = {}, signal) {
  const search = new URLSearchParams()
  if (params.majorGroup) search.set('majorGroup', params.majorGroup)
  if (params.targetGoal) search.set('targetGoal', params.targetGoal)
  if (params.sourcePlatform) search.set('sourcePlatform', params.sourcePlatform)
  if (params.profileMajor) search.set('profileMajor', params.profileMajor)
  const suffix = search.toString() ? `?${search.toString()}` : ''
  return request(`/student/project-templates${suffix}`, { signal })
}

export function getStudentProjectTemplate(templateId, signal) {
  return request(`/student/project-templates/${encodeURIComponent(templateId)}`, { signal })
}

export function generateStudentProjectPlan(templateId, payload = {}, signal) {
  return request('/project-opportunities/plans/generate', {
    method: 'POST',
    body: JSON.stringify({ ...payload, templateId }),
    signal,
  })
}

export function saveStudentProjectPlan(templateId, payload = {}, signal) {
  return request('/project-opportunities/plans/save', {
    method: 'POST',
    body: JSON.stringify({ ...payload, templateId }),
    signal,
  })
}

export function getStudentProjects(signal) {
  return request('/student-projects', { signal })
}

export function getStudentProject(projectId, signal) {
  return request(`/student-projects/${encodeURIComponent(projectId)}`, { signal })
}

export function createStudentProject(payload = {}, signal) {
  return request('/student-projects', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function updateStudentProject(projectId, payload = {}, signal) {
  return request(`/student-projects/${encodeURIComponent(projectId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    signal,
  })
}

export function addStudentProjectResult(projectId, payload = {}, signal) {
  return request(`/student-projects/${encodeURIComponent(projectId)}/results`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function generateStudentProjectResume(projectId, payload = {}, signal) {
  return request(`/student-projects/${encodeURIComponent(projectId)}/resume`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getProjectWorkspaceProjects(signal) {
  return request('/student-project-workspace/projects', { signal })
}

export function getProjectWorkspaceProject(projectId, signal) {
  return request(`/student-project-workspace/projects/${encodeURIComponent(projectId)}`, { signal })
}

export function updateProjectWorkspaceTask(projectId, taskId, payload = {}, signal) {
  return request(`/student-project-workspace/projects/${encodeURIComponent(projectId)}/tasks/${encodeURIComponent(taskId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    signal,
  })
}

export function submitProjectWorkspaceResult(projectId, payload = {}, signal) {
  return request(`/student-project-workspace/projects/${encodeURIComponent(projectId)}/results`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function getAdminProjectTemplates(signal) {
  return request('/admin/project-templates', { signal })
}

export function createAdminProjectTemplate(payload = {}, signal) {
  return request('/admin/project-templates', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function updateAdminProjectTemplate(templateId, payload = {}, signal) {
  return request(`/admin/project-templates/${encodeURIComponent(templateId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    signal,
  })
}

export function archiveAdminProjectTemplate(templateId, signal) {
  return request(`/admin/project-templates/${encodeURIComponent(templateId)}`, {
    method: 'DELETE',
    signal,
  })
}

export function getAdminExternalSourceItems(signal) {
  return request('/admin/external-source-items', { signal })
}

export function importAdminGithubSourceItems(payload = {}, signal) {
  return request('/admin/external-source-items/import-github', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function importAdminOpenAlexSourceItems(payload = {}, signal) {
  return request('/admin/external-source-items/import-openalex', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function importAdminProjectSourceUrl(url, signal) {
  return request('/admin/external-source-items/import-url', {
    method: 'POST',
    body: JSON.stringify({ url }),
    signal,
  })
}

export function convertAdminSourceItemToTemplate(sourceId, signal) {
  return request(`/admin/external-source-items/${encodeURIComponent(sourceId)}/convert-draft`, {
    method: 'POST',
    body: '{}',
    signal,
  })
}

export function generateInterviewSelfIntro(payload = {}, signal) {
  return request('/interview/generate-self-intro', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function generateInterviewProjectTalk(payload = {}, signal) {
  return request('/interview/generate-project-talk', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function generateInterviewQuestions(payload = {}, signal) {
  return request('/interview/generate-questions', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

export function simulateInterviewAnswer(payload = {}, signal) {
  return request('/interview/simulate', {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  })
}

